import * as React from 'react';

import { graphql, PageProps } from 'gatsby';
import Layout from 'core/components/Layout';
import SEO from 'core/components/SEO';
import type { BlogCardData } from 'blog/lib/types';
import BlogListHero from 'blog/components/BlogListHero';
import BlogEntries from 'blog/components/BlogEntries';
import type { OgImageQuery, PageContextWithMetaData } from 'lib/types';
import { canonicalizePathName } from 'blog/lib/util';
import MetaPagination from 'blog/components/MetaPagination';

type BlogListContext = PageContextWithMetaData & {
    slug: string;
    tag: string;
    tagName: string;
    category: string;
    categoryName: string;
    filter?: unknown;
    limit?: number;
    page: number;
    perPage: number;
    hasMore?: boolean;
};

type BlogListData = OgImageQuery & {
    entries: {
        nodes: ReadonlyArray<BlogCardData>;
    };
    tagsMetaData: {
        nodes: ReadonlyArray<{
            metaDescription: string;
            slug: string;
        }>;
    };
};

export default function BlogListTemplate({
    data: {
        entries: { nodes: entries },
        tagsMetaData: { nodes: tagsMetaData },
        ogImage,
    },
    location: { pathname },
    pageContext: {
        tag,
        tagName,
        category,
        categoryName,
        hasMore,
        page,
        perPage,
        title,
        description,
    },
}: PageProps<BlogListData, BlogListContext>) {
    // strip trailing "/all"s and trailing "/1"s from the blog list path name
    const canonicalPathName = canonicalizePathName(pathname);

    let seoTitle = title || 'Blog';

    const metaData = tagsMetaData.find((elem) => elem.slug === tag);
    let seoDescription = metaData?.metaDescription;
    if (!seoDescription) {
        seoDescription = description || '';
        if (tag && tag !== 'all') {
            seoDescription += ` | ${tagName || tag}`;
        }
    }

    if (category && category !== 'all') {
        seoTitle += ` | ${categoryName || category}`;
        seoDescription += ` | ${categoryName || category}`;
    }

    if (tag && tag !== 'all') {
        seoTitle += ` | ${tagName || tag}`;
    }

    if (page > 1) {
        seoTitle += ` | Page ${page}`;
        seoDescription += ` | Page ${page}`;
    }

    return (
        <Layout>
            <SEO
                title={seoTitle}
                pathname={canonicalPathName}
                description={seoDescription}
                ogImage={ogImage}
            />
            <BlogListHero />

            <BlogEntries
                selectedCategory={category}
                selectedTag={tag}
                entries={entries}
                page={page}
                perPage={perPage}
            />
            <MetaPagination
                tag={tag}
                category={category}
                page={page}
                hasMore={hasMore}
            />
        </Layout>
    );
}

export const blogQuery = graphql`
    query BlogListQuery(
        $categories: BlogCategoryFilterListInput
        $tags: BlogTagFilterListInput
    ) {
        entries: allBlogEntry(
            filter: {
                isDraft: { eq: false }
                categories: $categories
                tags: $tags
            }
            sort: { date: DESC }
        ) {
            nodes {
                ...BlogCardData
            }
        }
        tagsMetaData: allBlogTag {
            nodes {
                metaDescription
                slug
            }
        }
    }
`;
